import {
  AlertCircle,
  Clock,
  Cpu,
  RotateCcw,
  Shield,
  Wrench,
  Zap,
} from "lucide-react";
import type { RunResponse } from "../../gen/models";
import { normalizeAgentType } from "../../hooks/useUserDirectory";
import { TimeAgo } from "../common/TimeAgo";
import {
  extractRunCost,
  extractRunDuration,
  extractRunMetadata,
  extractRunModel,
  extractRunProvider,
  extractRunTokens,
  extractRunTools,
  extractRunTurns,
  formatCost,
  formatDuration,
  formatTokens,
} from "./runUtils";

interface RunListItemProps {
  run: RunResponse;
  isSelected: boolean;
  isDimmed: boolean;
  onClick: () => void;
}

export function RunListItem({
  run,
  isSelected,
  isDimmed,
  onClick,
}: RunListItemProps) {
  const model = extractRunModel(run);
  const provider = extractRunProvider(run);
  const duration = extractRunDuration(run);
  const cost = extractRunCost(run);
  const turns = extractRunTurns(run);
  const tools = extractRunTools(run);
  const { inTok, outTok, total, cached } = extractRunTokens(run);
  const { rulesCount, isBlocked, isSimulation, isTestRun, errorMessage } = extractRunMetadata(run);

  return (
    <div
      onClick={onClick}
      className={`px-3.5 py-3 border-b border-l-2 text-xs cursor-pointer transition-all duration-150 space-y-2 ${
        isSelected
          ? "bg-white dark:bg-stone-900 border-l-stone-900 dark:border-l-stone-100 border-b-stone-200 dark:border-b-stone-800 shadow-2xs opacity-100"
          : `border-l-transparent border-b-stone-200/80 dark:border-b-stone-800/60 hover:bg-stone-100/70 dark:hover:bg-stone-800/50 ${
              isDimmed ? "opacity-50 hover:opacity-100" : "opacity-100"
            }`
      }`}
    >
      {/* Top Header: Agent, Badges, Status, Started Time */}
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-stone-900 dark:bg-stone-800 text-white shrink-0">
            {normalizeAgentType(run.agent_type)}
          </span>

          {isTestRun && (
            <span className="px-1.5 py-0.2 rounded text-[9.5px] font-semibold bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 shrink-0">
              Test Run
            </span>
          )}

          {isSimulation && (
            <span className="px-1.5 py-0.2 rounded text-[9.5px] font-semibold bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 shrink-0">
              Simulation
            </span>
          )}

          {isBlocked && (
            <span className="px-1.5 py-0.2 rounded text-[9.5px] font-semibold bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 shrink-0">
              Blocked
            </span>
          )}

          <TimeAgo
            date={run.started_at}
            titlePrefix="Started: "
            className="text-[10px] text-stone-400 dark:text-stone-500 font-mono truncate"
          />
        </div>

        <span
          className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold capitalize border shrink-0 ${
            run.status === "completed"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800"
              : run.status === "running"
              ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800 animate-pulse"
              : run.status === "simulation"
              ? "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/60 dark:text-purple-400 dark:border-purple-800"
              : "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-400 dark:border-rose-800"
          }`}
        >
          {run.status}
        </span>
      </div>

      {/* Entry Point, Temporal Workflow ID, and Thread ID */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div
          className="font-mono text-[11px] font-semibold text-stone-800 dark:text-stone-200 truncate"
          title={run.entry_point}
        >
          {run.entry_point}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {(run.telemetry as any)?.temporal_workflow_id && (
            <span
              className="font-mono text-[9px] text-sky-700 dark:text-sky-400 bg-sky-50 dark:bg-sky-950/60 px-1 py-0.2 rounded border border-sky-200 dark:border-sky-800 shrink-0 truncate max-w-[120px]"
              title={`Temporal: ${(run.telemetry as any).temporal_workflow_id}`}
            >
              {(run.telemetry as any).temporal_workflow_id}
            </span>
          )}
          {run.thread_id && (
            <span
              className="font-mono text-[10px] text-stone-400 dark:text-stone-500 shrink-0 hover:text-stone-600 dark:hover:text-stone-300"
              title={`Thread: ${run.thread_id}`}
            >
              #{run.thread_id.slice(0, 8)}
            </span>
          )}
        </div>
      </div>

      {/* Core Metrics: Model, Provider, Latency, Turns */}
      <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
        {model && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 font-mono text-[10px] font-medium border border-stone-200/70 dark:border-stone-700/70 max-w-[190px] truncate"
            title={`Model: ${model}${provider ? ` (${provider})` : ""}`}
          >
            <Cpu className="w-2.5 h-2.5 text-stone-500 dark:text-stone-400 shrink-0" />
            <span className="truncate">{model}</span>
          </span>
        )}

        {provider && (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50/80 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 font-mono text-[9.5px] font-medium border border-amber-200/60 dark:border-amber-800/60 shrink-0"
            title={`Provider: ${provider}`}
          >
            {provider}
          </span>
        )}

        {duration !== undefined && (
          <span
            className="inline-flex items-center gap-0.5 font-mono text-[10px] text-stone-600 dark:text-stone-400 bg-stone-100/60 dark:bg-stone-800/40 px-1.5 py-0.5 rounded border border-stone-200/50 dark:border-stone-800/60"
            title={`Duration / Latency: ${duration}ms`}
          >
            <Clock className="w-2.5 h-2.5 text-stone-400 shrink-0" />
            <span>{formatDuration(duration)}</span>
          </span>
        )}

        {turns !== undefined && (
          <span
            className="inline-flex items-center gap-0.5 font-mono text-[10px] text-stone-600 dark:text-stone-400 bg-stone-100/60 dark:bg-stone-800/40 px-1.5 py-0.5 rounded border border-stone-200/50 dark:border-stone-800/60"
            title={`Execution turns: ${turns}`}
          >
            <RotateCcw className="w-2.5 h-2.5 text-stone-400 shrink-0" />
            <span>
              {turns} {turns === 1 ? "turn" : "turns"}
            </span>
          </span>
        )}
      </div>

      {/* Secondary Metrics: Tokens (Prompt/Completion/Cached) & Cost */}
      <div className="flex items-center justify-between text-[10px] text-stone-500 dark:text-stone-400 pt-1 border-t border-stone-100 dark:border-stone-800/60">
        <div className="flex flex-wrap items-center gap-1.5 min-w-0">
          <span
            className="inline-flex items-center gap-0.5 font-mono"
            title={`Tokens: ${total.toLocaleString()} total (${inTok.toLocaleString()} in / ${outTok.toLocaleString()} out)`}
          >
            <Zap className="w-2.5 h-2.5 text-amber-500 shrink-0" />
            <span>{formatTokens(total)} toks</span>
            {(inTok > 0 || outTok > 0) && (
              <span className="text-stone-400 dark:text-stone-500 text-[9px]">
                ({formatTokens(inTok)}/{formatTokens(outTok)})
              </span>
            )}
          </span>

          {cached > 0 && (
            <span
              className="inline-flex items-center gap-0.5 font-mono text-[9.5px] text-indigo-600 dark:text-indigo-400 bg-indigo-50/70 dark:bg-indigo-950/40 px-1 py-0.2 rounded border border-indigo-200/60 dark:border-indigo-900/60"
              title={`${cached.toLocaleString()} cached prompt tokens`}
            >
              <span>⚡{formatTokens(cached)} cache</span>
            </span>
          )}

          {rulesCount > 0 && (
            <span
              className="inline-flex items-center gap-0.5 font-mono text-[9.5px] text-blue-600 dark:text-blue-400 bg-blue-50/70 dark:bg-blue-950/40 px-1 py-0.2 rounded border border-blue-200/60 dark:border-blue-900/60"
              title={`${rulesCount} active rules applied`}
            >
              <Shield className="w-2.5 h-2.5 shrink-0" />
              <span>{rulesCount} rules</span>
            </span>
          )}
        </div>

        {cost > 0 ? (
          <span
            className="font-mono font-semibold text-[10px] text-emerald-600 dark:text-emerald-400 bg-emerald-50/80 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded border border-emerald-200/60 dark:border-emerald-800/60 shrink-0 ml-1"
            title={`Estimated cost: $${cost}`}
          >
            {formatCost(cost)}
          </span>
        ) : (
          <span className="font-mono text-[10px] text-stone-400 dark:text-stone-500 shrink-0 ml-1">
            $0.00
          </span>
        )}
      </div>

      {/* Tools Invoked List */}
      {tools.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 pt-1 border-t border-stone-100/80 dark:border-stone-800/40">
          <span className="inline-flex items-center gap-0.5 text-[9.5px] text-stone-400 dark:text-stone-500 font-medium shrink-0">
            <Wrench className="w-2.5 h-2.5 text-amber-500 shrink-0" />
            <span>Tools:</span>
          </span>
          {tools.slice(0, 2).map((tool) => (
            <code
              key={tool}
              className="px-1.5 py-0.2 rounded bg-amber-50/80 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/60 text-[9.5px] font-mono font-medium truncate max-w-[130px]"
              title={tool}
            >
              {tool}
            </code>
          ))}
          {tools.length > 2 && (
            <span
              className="px-1 py-0.2 rounded bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 text-[9px] font-mono font-semibold"
              title={`Additional tools: ${tools.slice(2).join(", ")}`}
            >
              +{tools.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Error Message preview if failed */}
      {errorMessage && (
        <div
          className="text-[10px] font-mono text-rose-700 dark:text-rose-300 bg-rose-50/80 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded px-2 py-1 truncate"
          title={errorMessage}
        >
          <AlertCircle className="w-2.5 h-2.5 inline mr-1 text-rose-500 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
}
